import {
  Button,
  Card,
  Col,
  Descriptions,
  Dropdown,
  Popconfirm,
  Row,
  Space,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import { useMemo, useState } from 'react';
import { BsBox2 } from 'react-icons/bs';
import { CiLocationOn } from 'react-icons/ci';
import { IoDocumentTextOutline, IoWarningOutline } from 'react-icons/io5';
import { MdDateRange } from 'react-icons/md';
import { TbTruckDelivery } from 'react-icons/tb';

import imgMore from '../../../assets/img/common/fi_more.svg';
import { getOrderStatus } from '../../../utils';
import { isHandledByInterceptor } from '../../../utils/apiError';
import { formatCurrency, getDateTime, getTime, getYmd } from '../../../utils/format';
import { exportToXLSX } from '../../../utils/xlsxHelper';
import { DefaultProcurementExcelColumnEN, DefaultProcurementExcelColumnMN } from '../utils';
import ProcurementProductTable from './ProcurementProductTable';
import '../Status.less';
import './style.less';

const { Title } = Typography;

/** Хоосон утгыг "-" болгож харуулна */
const val = (value) => (value === null || value === undefined || value === '' ? '-' : value);

/** Descriptions-ийн ерөнхий тохиргоо: label дээр, value доор, зай багатай */
const descriptionProps = {
  size: 'small',
  colon: false,
  layout: 'vertical',
  column: { xs: 1, sm: 2 },
  labelStyle: { paddingBottom: 0 },
  contentStyle: { paddingBottom: 8 },
};

/** Картын толгой: дүрс + текст */
const CardTitle = ({ icon: Icon, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
    <Icon style={{ display: 'block', fontSize: '1em' }} />
    {children}
  </span>
);

/**
 * Захиалгын төлвийн badge — өнгө нь Status.less дэх төлвийн класснаас гарна.
 * Жагсаалт (List.js) дээрхтэй ижил төрх.
 */
const StatusBadge = ({ status, label }) => {
  if (!status) return null;

  return (
    <span className={`procurement-status-badge procurement-status-${status}`}>
      <span className="procurement-status-dot" />
      {label ?? status}
    </span>
  );
};

export const OrderDetail = ({ detail = {}, onRefresh }) => {
  const [canceling, setCanceling] = useState(false);

  /**
   * Захиалгыг цуцлана. Хүргэлтийн захиалгын дэлгэрэнгүйтэй (orderDelivery/
   * ListWithDetail/Detail.js) ижил `statusChange` үйлчилгээг ашиглана —
   * татан авалтын дэлгэрэнгүйд энэ гарц огт байгаагүй тул үүсгэсэн захиалгаа
   * буцаах боломжгүй байв.
   */
  const onCancelOrder = async () => {
    setCanceling(true);
    try {
      const { data } = await axios.post('/api/v1/request/statusChange', {
        orderId: detail?.code,
        orderStatus: 'canceled',
      });

      if (data?.statusCode === 200) {
        message.success('Захиалга цуцлагдлаа.');
        onRefresh?.();
        return;
      }

      // Interceptor мэдэгдээгүй кодод л локал мэдэгдэл гаргана — эс тэгвээс давхардана
      if (!isHandledByInterceptor(data)) {
        message.error(data?.message_mn ?? data?.message ?? 'Захиалга цуцлахад алдаа гарлаа.');
      }
    } catch (error) {
      // Мэдэгдлийг axios-ийн global interceptor гаргана — энд давхардуулахгүй
      console.error('Cancel procurement order: ', error);
    } finally {
      setCanceling(false);
    }
  };

  const exportExcel = (orders = []) => {
    orders.forEach((order) => (order.productBarCodes = order.productBarCodes?.join?.(',')));
    exportToXLSX({
      data: orders,
      columns: DefaultProcurementExcelColumnEN,
      columnNames: DefaultProcurementExcelColumnMN,
      fileName: `Татан авалт ${getDateTime(detail?.createdAt || new Date())}`,
    });
  };

  const menuItems = useMemo(
    () => [
      {
        key: 'export',
        label: 'Excel татах',
        onClick: () => exportExcel(detail.orders || []),
      },
    ],
    //eslint-disable-next-line
    [detail]
  );

  const statusText = getOrderStatus(detail?.meta?.orderStatus?.detail, detail?.orderStatus);

  /** Машины төрлийн нэр */
  const pricingName = (detail?.meta?.pricing?.types ?? [])
    .filter((type) => type === detail?.pricingCode)
    .map((type) => detail?.meta?.pricing?.detail?.[type]?.name)
    .join(', ');

  const hasActions = ['ARP', 'MFD'].includes(detail?.requesterCode);

  /** Зөвхөн үүсгэсэн төлөвт цуцлана — цааш явсан захиалгыг агуулах боловсруулж эхэлсэн */
  const cancelButton =
    detail?.orderStatus === 'created' ? (
      <Popconfirm
        placement="bottomRight"
        title="Энэ захиалгыг цуцлах уу?"
        okText="Тийм"
        cancelText="Үгүй"
        onConfirm={onCancelOrder}
      >
        <Button danger loading={canceling}>
          Цуцлах
        </Button>
      </Popconfirm>
    ) : null;

  const actions = (
    <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
      <Button icon={<img src={imgMore} alt="Бусад үйлдэл" height={15} />} />
    </Dropdown>
  );

  const noteCard = (
    <Card
      title={<CardTitle icon={IoWarningOutline}>Анхаарах шаардлагатай</CardTitle>}
      size="middle"
    >
      <Descriptions
        {...descriptionProps}
        column={1}
        items={[
          {
            key: 'notes',
            label: 'Тэмдэглэл',
            children: val(detail?.notes?.length > 0 ? detail.notes.join(', ') : null),
          },
          {
            key: 'description',
            label: 'Нэмэлт тэмдэглэл',
            children: val(detail?.orderDescription),
          },
        ]}
      />
    </Card>
  );

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
      <Card
        size="middle"
        title={
          <Title level={5} style={{ margin: 0 }}>
            {val(detail?.code)}
          </Title>
        }
        extra={
          cancelButton || hasActions ? (
            <Space wrap>
              {cancelButton}
              {hasActions ? actions : null}
            </Space>
          ) : null
        }
      >
        <Descriptions
          {...descriptionProps}
          column={{ xs: 1, sm: 3 }}
          items={[
            {
              key: 'code',
              label: 'Захиалгын дугаар',
              children: val(detail?.code),
            },
            {
              key: 'date',
              label: 'Үүссэн огноо',
              children: val(detail?.createdAt ? getDateTime(detail.createdAt) : null),
            },
            {
              key: 'status',
              label: 'Захиалгын төлөв',
              children: <StatusBadge status={detail?.orderStatus} label={statusText} />,
            },
          ]}
        />
      </Card>

      <Card
        title={<CardTitle icon={BsBox2}>Барааны жагсаалт</CardTitle>}
        size="middle"
        styles={{ body: { padding: 0 } }}
      >
        <ProcurementProductTable products={detail?.orders ?? []} status={detail?.orderStatus} />
      </Card>

      <Row gutter={[16, 16]} align="top">
        <Col xs={24} xl={12}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card
              title={<CardTitle icon={CiLocationOn}>Татан авах байршил</CardTitle>}
              size="middle"
            >
              <Descriptions
                {...descriptionProps}
                column={1}
                items={[
                  {
                    key: 'branch',
                    label: 'Салбар',
                    children: val(detail?.pickupAddressCode),
                  },
                ]}
              />
            </Card>

            <Card
              title={<CardTitle icon={MdDateRange}>Татан авах хугацаа</CardTitle>}
              size="middle"
            >
              <Descriptions
                {...descriptionProps}
                items={[
                  {
                    key: 'date',
                    label: 'Татах огноо',
                    children: val(detail?.pickupDate ? getYmd(detail.pickupDate) : null),
                  },
                  {
                    key: 'time',
                    label: 'Татах цаг',
                    children: val(detail?.pickupDate ? getTime(detail.pickupDate) : null),
                  },
                ]}
              />
            </Card>

            {noteCard}
          </Space>
        </Col>

        <Col xs={24} xl={12}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card title={<CardTitle icon={IoDocumentTextOutline}>Төлбөр</CardTitle>} size="middle">
              <Descriptions
                {...descriptionProps}
                items={[
                  {
                    key: 'car',
                    label: 'Машин',
                    children: val(pricingName),
                  },
                  {
                    key: 'amount',
                    label: 'Төлбөр /НӨАТ-гүй/',
                    children: val(
                      detail?.totalServiceAmount
                        ? formatCurrency(parseInt(detail.totalServiceAmount))
                        : null
                    ),
                  },
                ]}
              />
            </Card>

            <Card title={<CardTitle icon={TbTruckDelivery}>Жолооч</CardTitle>} size="middle">
              <Descriptions
                {...descriptionProps}
                items={[
                  {
                    key: 'name',
                    label: 'Нэр',
                    children: val(detail?.currentHolderName),
                  },
                  {
                    key: 'phone',
                    label: 'Утас',
                    children: val(detail?.currentHolderPhone),
                  },
                ]}
              />
            </Card>
          </Space>
        </Col>
      </Row>
    </Space>
  );
};
